import { useEffect, useState } from "react";
import { getAllAgreements } from "../services/agreementsService";
import CardAgreement from "../components/agreements/AgreementCard";
import { Typography, Button, Pagination } from "@mui/material";
import { useAuth } from "../context/authContext";
import { getAllCompanies } from "../services/companiesService";
import { ActionLoader, PageLoader } from "../components/loader";
import { Banner } from "../components/common/Banner";
import AgreementForm from "../components/agreements/AgreementForm";
import SectionTips from "../components/common/SectionTips";

const Agreement = () => {
    const [agreements, setAgreements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [openModal, setOpenModal] = useState(false);
    const { user } = useAuth();
    const agreementPerPage = 12;
    const [companies, setCompanies] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const response = await getAllCompanies(user);
            setCompanies(response.map(company => company.business_name)); 
            setLoading(false);
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAgreements = async () => {
        try {
            setLoading(true);
            const agreements = await getAllAgreements(user);
            setAgreements(agreements);
        } catch (error) {
            setError(error);
            console.error("Error fetching agreements:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAgreements();
        fetchCompanies();
    }, []);

    if (loading) {
        return (
            <PageLoader message="Cargando convenios..." />
        );
    }
    
    if (error) return <div>Error: {error.message}</div>;

    const totalPages = Math.ceil(agreements.length / agreementPerPage);
    const sortedAgreements = [...agreements].sort((a, b) => b.id_agreement - a.id_agreement);
    const paginatedAgreements = sortedAgreements.slice(
        (currentPage - 1) * agreementPerPage,
        currentPage * agreementPerPage
    );

    const handleOpenModal = () => {
        setOpenModal(true);
    };


    return (
      <div className="min-h-screen bg-gray-100">
        <ActionLoader open={isSubmitting} onClose={() => {}}></ActionLoader>
        <main className="flex flex-col items-center py-8 px-4 sm:px-8">
          <div className="w-full max-w-7xl ">
            <Banner 
              title="Convenios Disponibles" 
              message="Beneficios exclusivos para nuestra comunidad"
            />

            <SectionTips
              section="la sección de convenios"
              buttonTitle="¿Cómo usar esta sección?"
              tipsTitle="¿Cómo visualizar los convenios?"
              tipsList={[
                'Revisa el detalle de cada convenio disponible haciendo click en el.',
                'Encontraras la información de contacto y los beneficios que ofrece cada convenio.',
                'Si el convenio es de tu interés, puedes contactarte directamente con la empresa para más información.',
              ]}
              color="#1976d2" // tu color institucional si usas otro
            />
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between w-full max-w-7xl mb-6 space-y-4 md:space-y-0">
            {user?.role === 'admin' && (
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(to right, #09274A, #0488AF)',
                  color: '#fff',
                  textTransform: 'none',
                  minWidth: 140,
                  minHeight: '36px',
                  padding: '6px 16px',
                  lineHeight: '1.5',
                  '&:hover': {
                    background: 'linear-gradient(to right, #071f3d, #03779C)',
                  },
                }}
                onClick={handleOpenModal}
              >
                Agregar convenio
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-7xl">
            {paginatedAgreements.length > 0 ? (
              paginatedAgreements.map((agreement) => (
                <CardAgreement
                  key={agreement.id_agreement}
                  agreement={agreement}
                  isAdmin={user?.role === 'admin'}
                  onUpdate={fetchAgreements}
                />
              ))
            ) : (
              <Typography variant="h6" color="textSecondary" align="center" sx={{ width: '100%', mt: 4 }}>
                No hay convenios creados
              </Typography>
            )}
          </div>

          {agreements.length > 0 && (
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              color="primary" 
              sx={{ mt: 4 }} 
              onChange={(_, page) => setCurrentPage(page)} 
            />
          )}
        </main>

        {/* Componente de formulario de acuerdo */}
        <AgreementForm 
          open={openModal}
          onClose={() => setOpenModal(false)}
          companies={companies}
          user={user}
          onSuccess={fetchAgreements}
          onActionLoader={setIsSubmitting}
        />
      </div>
    );
};

export default Agreement;